import { forwardRef } from "react";
import { Card } from "@/components/ui/card";
import BayRow from "./BayRow";
import EmptyBays from "./EmptyBays";
import NowIndicator from "./NowIndicator";
import BlackoutOverlay from "./BlackoutOverlay";
import { fmtHour, fmtDayLabel, isSiteMidnight } from "./formatters";

export const BAY_LABEL_WIDTH = 180;
const ROW_HEIGHT = 64;

const TimeHeader = ({ hourTicks, pxPerHour }) => (
  <div className="sticky top-0 z-20 flex bg-white border-b" style={{ paddingLeft: BAY_LABEL_WIDTH }}>
    {hourTicks.slice(0, -1).map(({ idx, date }) => (
      <div key={idx} className="border-l first:border-l-0 text-[11px] text-slate-500" style={{ width: pxPerHour }}>
        <div className="px-1.5 pt-1 pb-0.5 font-medium tabular-nums">{fmtHour(date)}</div>
        {(idx === 0 || isSiteMidnight(date)) && (
          <div className="px-1.5 pb-1 text-[10px] uppercase tracking-wider text-slate-400">{fmtDayLabel(date)}</div>
        )}
      </div>
    ))}
  </div>
);

// Scrollable bay × time grid. `data` = useDockData, `model` = useGridModel.
const DockGrid = forwardRef(function DockGrid(
  { data, model, windowStart, windowEnd, pxPerHour, horizonHours, onSlotClick, onBookingClick }, scrollRef
) {
  const { bays, bayBuffers, clientBufferEnabled, siteBufferDefault } = data;
  const range = { start: windowStart, end: windowEnd, left: BAY_LABEL_WIDTH, pxPerHour };
  return (
    <Card className="overflow-hidden">
      <div ref={scrollRef} className="overflow-x-auto" data-testid="dock-grid-scroll">
        <div className="relative" style={{ width: `${BAY_LABEL_WIDTH + horizonHours * pxPerHour}px` }}>
          <TimeHeader hourTicks={model.hourTicks} pxPerHour={pxPerHour} />
          <NowIndicator {...range} />
          {model.blackoutsByBay.siteWide.map((bk) => (
            <BlackoutOverlay key={bk.id} blackout={bk} {...range} top={42}
                             rowHeight={ROW_HEIGHT} fullHeight={bays.length * ROW_HEIGHT} />
          ))}
          <div>
            {bays.length === 0 ? <EmptyBays /> : bays.map((bay) => (
              <BayRow
                key={bay.id}
                bay={{ ...bay, buffer_minutes: bayBuffers.get(bay.id) ?? null }}
                windowStart={windowStart}
                windowEnd={windowEnd}
                pxPerHour={pxPerHour}
                horizonHours={horizonHours}
                rowHeight={ROW_HEIGHT}
                labelWidth={BAY_LABEL_WIDTH}
                bookings={model.bookingsByBay.get(bay.id) || []}
                blackouts={model.blackoutsByBay.byBay.get(bay.id) || []}
                patternGhosts={model.patternGhostsByBay.get(bay.id) || []}
                hourTicks={model.hourTicks}
                onSlotClick={onSlotClick}
                onBookingClick={onBookingClick}
                clientBufferEnabled={clientBufferEnabled}
                siteDefaultBuffer={siteBufferDefault}
                closed={model.closedByBay.get(bay.id) || []}
              />
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
});

export default DockGrid;
